export type FontSize = '14px' | '16px' | '18px' | '20px'
export type FontFamily =
  | 'Arial'
  | 'Helvetica'
  | 'Georgia'
  | 'Times New Roman'
  | 'Courier New'
  | 'Verdana'
export type PageWidth = 'narrow' | 'wide' | 'full'
export type AutoSaveFrequency = 0 | 30 | 60 | 120 | 300 // seconds
export type Theme =
  | 'light'
  | 'dark'
  | 'rose'
  | 'sepia'
  | 'ocean'
  | 'midnight'
  | 'green'
  | 'purple'
  | 'yellow'
  | 'rainbow'
  | 'aurora'
  | 'embers'
  | 'nebula'
  | 'parchment'
  | 'jade'
  | 'crimson'
  | 'glacier'
  | 'dusk'
export type IndentSize = 0 | 24 | 48 | 96
export type ParagraphSpacing = 'normal' | 'wide' | 'narrow'
export type TextAlign = 'left' | 'center' | 'right' | 'justify'

/** Block style shown in the editor toolbar (labels are Spanish in the UI). */
export type BlockType = 'paragraph' | 'heading-1' | 'heading-2' | 'heading-3'

export const BLOCK_TYPE_OPTIONS: Array<{ value: BlockType; label: string }> = [
  { value: 'paragraph', label: 'Texto' },
  { value: 'heading-1', label: 'Encabezado 1' },
  { value: 'heading-2', label: 'Encabezado 2' },
  { value: 'heading-3', label: 'Encabezado 3' },
]

export function blockTypeToHeadingLevel(blockType: BlockType): 1 | 2 | 3 | null {
  if (blockType === 'heading-1') return 1
  if (blockType === 'heading-2') return 2
  if (blockType === 'heading-3') return 3
  return null
}

export interface EditorSettings {
  fontSize: FontSize
  fontFamily: FontFamily
  lineHeight: number
  pageWidth: PageWidth
  autoSave: boolean
  autoSaveFrequency: AutoSaveFrequency
  hideSaveStatus: boolean
  hideCountStats: boolean
  showMobileSaveStatus: boolean
  showMobileCountStats: boolean
  showMobileWritingSession: boolean
  theme: Theme
  showWordCount: boolean
  spellCheck: boolean
  indentSize: IndentSize
  paragraphSpacing: ParagraphSpacing
}

export interface EditorState {
  isBold: boolean
  isItalic: boolean
  isUnderline: boolean
  isStrike: boolean
  fontSize: FontSize
  fontFamily?: FontFamily
  textAlign: TextAlign | null
  blockType: BlockType
  insertText?: string
  insertParagraphSeparator?: boolean
  insertHorizontalRule?: boolean
  undo?: boolean
  redo?: boolean
  canUndo?: boolean
  canRedo?: boolean
}

export type SavingState =
  | 'updated' // Sincronizado con servidor
  | 'updating' // Guardando en servidor
  | 'pending changes' // Cambios locales no guardados
  | 'local-backup' // Solo respaldo local disponible
  | 'offline' // Modo offline activo
  | 'conflict' // Conflicto detectado
  | 'error' // Error al cargar sin respaldo local

// Chapter backup structure for offline editor storage (IndexedDB via storage layer)
export interface ChapterBackup {
  title: string
  content?: string
  jsonContent?: object
  timestamp: number // UTC timestamp
  projectId: string
  chapterId: string
  savedAt: string // ISO string for debugging
  sourceId: string // Unique UUID per tab/session
  /** Present on backups saved after this change. Legacy entries omit it. */
  userId?: string
  /** Last known server updatedAt (informative; not used for conflict detection). */
  baseUpdatedAt?: string
  /** Last confirmed server contentVersion for optimistic concurrency. */
  baseContentVersion?: number
  /** Save ids sent without a resolved ACK (max 10, persisted with backup). */
  inDoubtSaveIds?: string[]
  /** False when this record mirrors the server. Missing or true means unsynced edits. */
  pendingSync?: boolean
  syncPhase?: import('@/utils/chapterSyncPhase').ChapterSyncPhase
  lastSuccessfulSyncAt?: number
  lastErrorCode?: string
  lastErrorMessage?: string
  /** When true, storage cleanup must keep this copy. */
  retained?: boolean
  /** Chapter position in the project, when known. */
  order?: number
  /** When `title`, background PATCH omits jsonContent (title-only edits). */
  patchScope?: 'title' | 'full'
}

// Metadata for offline backup storage management
export interface LocalStorageMetadata {
  lastCleanup: number
  version: string
}

export const DEFAULT_EDITOR_STATE: EditorState = {
  isBold: false,
  isItalic: false,
  isUnderline: false,
  isStrike: false,
  textAlign: null,
  blockType: 'paragraph',
  fontSize: '16px' as FontSize,
  fontFamily: 'Arial' as FontFamily,
}

export const DEFAULT_EDITOR_SETTINGS: EditorSettings = {
  fontSize: '16px' as FontSize,
  fontFamily: 'Arial' as FontFamily,
  lineHeight: 1.5,
  pageWidth: 'wide' as PageWidth,
  autoSave: true,
  autoSaveFrequency: 0,
  theme: 'light' as Theme,
  showWordCount: true,
  spellCheck: true,
  indentSize: 48 as IndentSize,
  paragraphSpacing: 'normal' as ParagraphSpacing,
  hideSaveStatus: false,
  hideCountStats: false,
  showMobileSaveStatus: false,
  showMobileCountStats: false,
  showMobileWritingSession: false,
}

export const FONT_SIZE_OPTIONS = [
  { label: 'Pequeño', value: '14px' as FontSize },
  { label: 'Normal', value: '16px' as FontSize },
  { label: 'Grande', value: '18px' as FontSize },
  { label: 'Muy grande', value: '20px' as FontSize },
]

export const FONT_FAMILY_OPTIONS = [
  { label: 'Arial', value: 'Arial' as FontFamily },
  { label: 'Helvetica', value: 'Helvetica' as FontFamily },
  { label: 'Georgia', value: 'Georgia' as FontFamily },
  { label: 'Times New Roman', value: 'Times New Roman' as FontFamily },
  { label: 'Courier New', value: 'Courier New' as FontFamily },
  { label: 'Verdana', value: 'Verdana' as FontFamily },
]

export const LINE_HEIGHT_OPTIONS = [
  { label: 'Compacto', value: 1.2 },
  { label: 'Normal', value: 1.5 },
  { label: 'Espaciado', value: 1.8 },
  { label: 'Muy espaciado', value: 2.0 },
]

export const PAGE_WIDTH_OPTIONS = [
  { label: 'Estrecho', value: 'narrow' as PageWidth },
  { label: 'Ancho', value: 'wide' as PageWidth },
  { label: 'Completo', value: 'full' as PageWidth },
]

export const AUTO_SAVE_FREQUENCY_OPTIONS = [
  { label: 'Inmediato', value: 0 as AutoSaveFrequency },
  { label: '30 segundos', value: 30 as AutoSaveFrequency },
  { label: '1 minuto', value: 60 as AutoSaveFrequency },
  { label: '2 minutos', value: 120 as AutoSaveFrequency },
  { label: '5 minutos', value: 300 as AutoSaveFrequency },
]

export const INDENT_SIZE_OPTIONS = [
  { label: 'Sin sangría', value: 0 as IndentSize },
  { label: 'Poca', value: 24 as IndentSize },
  { label: 'Intermedia', value: 48 as IndentSize },
  { label: 'Grande', value: 96 as IndentSize },
]

export const PARAGRAPH_SPACING_OPTIONS = [
  { label: 'Estrecho', value: 'narrow' as ParagraphSpacing },
  { label: 'Normal', value: 'normal' as ParagraphSpacing },
  { label: 'Ancho', value: 'wide' as ParagraphSpacing },
]
